import { useState, useRef, useEffect } from 'react';
import { LogOut, User, ChevronDown, LayoutDashboard } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@workspace/web-ui/components/button';
import { useAuthModal } from '../pages/welcome/store/use-auth-modal';
import { AuthModal } from './auth-modal';

import useAuthFormStore from '../pages/welcome/store/use-auth-form-store';
import { AuthUser } from '~/types';
interface UserMenuProps {
  user: AuthUser | null | undefined;
  isAuthenticated: boolean;
  logout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, isAuthenticated, logout }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { open: openAuthModal } = useAuthModal();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { setStep } = useAuthFormStore();

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!isAuthenticated || !user) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end sm:gap-4 space-y-3 sm:space-y-0">
        <Button
          className="sm:inline-flex flex  rounded-full !bg-black !bg-opacity-0 border border-[#ffffff] hover:bg-slate-400 sm:h-11 sm:mx-3 px-7 py-5 text-lg font-semibold text-white sm:bg-white sm:text-sm"
          onClick={() => {
            setStep(1);
            openAuthModal();
          }}
        >
          LOGIN
        </Button>
        <Link
          to="/welcome"
          className="inline-block rounded-full bg-blue-600 px-6 py-3 text-lg font-semibold text-white sm:bg-white sm:text-sm sm:text-blue-600"
        >
          GET STARTED
        </Link>
        <AuthModal />
      </div>
    );
  }

  return (
    <div>
      <div className="relative" ref={dropdownRef}>
        {/* Account Button (visible on smaller screens) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden flex items-center space-x-2 rounded-2xl bg-blue-600 px-6 py-3 text-white hover:bg-blue-600 transition-colors focus:outline-none"
        >
          <User className="h-4 w-4 mr-1" />
          <span className="font-medium">My Account</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* User Avatar (visible on medium screens and up) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="hidden md:flex items-center space-x-2 focus:outline-none"
        >
          <div className="flex size-12 items-center justify-center rounded-full border-2 border-green-500 bg-green-500 font-bold text-white transition-all hover:bg-green-600">
            {getInitials(`${user.first_name} ${user.last_name}`)}
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown Menu (same for both views) */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            <div className="py-1">
              <div className="block px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                <p className="font-medium">{`${user.first_name} ${user.last_name}`}</p>
                <p className="text-gray-500 text-xs mt-1 truncate">{user.email}</p>
              </div>
              <Link
                to="/dashboard"
                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Dashboard
              </Link>
              <button
                onClick={logout}
                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserMenu;